import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { isCommunicationTarget, useOverlayPortal } from "@/lib/overlayPortal"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

// 沟通窗中的转发、成员等弹窗跟随沟通顶层，避免被自己的聊天面板挡住。
const DialogPortal = (props) => <DialogPrimitive.Portal container={useOverlayPortal() || undefined} {...props} />

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-slate-900/45 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * 共用弹窗：手机保留外边距，关闭入口始终位于本弹窗的焦点与层级范围内。
 * 原关闭按钮单独挂在 Portal，既被 Radix 的 hideOthers/焦点锁排除，也会被市检、
 * 派工等 z-[10001] 弹窗盖住。现在放回 Content 内，不再猜测全站最高 z-index。
 *
 * 不能给所有正文再包一层：PDF 的 grid-rows、权限编辑的 flex，以及搜索的 p-0
 * 都依赖现有直接子项。关闭按钮因此采用不占布局的绝对定位，并只补偿 Content
 * 自身的滚动距离；内嵌列表滚动不会影响它，也不需要滚动时重新渲染整张表单。
 */
const DialogContent = React.forwardRef(({ className, children, overlayClassName, onScroll, onInteractOutside, closeLabel = "Close", closeDisabled = false, closeTestId, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      data-safe-area-dialog=""
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid grid-cols-1 w-full max-w-lg overflow-y-auto gap-4 translate-x-[-50%] translate-y-[-50%] border border-slate-200/80 bg-white shadow-2xl shadow-slate-900/20 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] w-[calc(100%-1rem)] max-h-[min(92dvh,calc(100dvh-2rem-env(safe-area-inset-top)-env(safe-area-inset-bottom)))] overscroll-contain rounded-2xl [&_[cmdk-input-wrapper]]:pr-14",
        // 说明：padding 不再写死，改由调用方传入（默认 p-4 sm:p-6），这样 GlobalSearch 等
        // 需要「无内边距」的弹窗只需传 p-0 覆盖即可，无需为覆盖默认值在 cn 里产生类名竞争。
        "p-4 sm:p-6",
        className
      )}
      {...props}
      onInteractOutside={(event) => {
        // 沟通入口跨页面常驻；打开聊天不等于放弃正在填写的合同/派工表单。
        if (isCommunicationTarget(event.target)) event.preventDefault();
        onInteractOutside?.(event);
      }}
      onScroll={(event) => {
        if (event.target === event.currentTarget) {
          event.currentTarget.style.setProperty("--dialog-scroll-y", `${Math.max(0, event.currentTarget.scrollTop)}px`);
        }
        onScroll?.(event);
      }}>
      {children}
      <DialogPrimitive.Close
        data-testid={closeTestId}
        data-dialog-close=""
        // 业务弹窗可提供本地化名称及提交锁，避免再叠一枚关闭按钮、或者忙碌时误导用户。
        aria-label={closeLabel}
        disabled={closeDisabled}
        style={{ transform: "translateY(var(--dialog-scroll-y, 0px))" }}
        className="absolute right-1 top-1 z-50 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/95 shadow-sm text-slate-500 backdrop-blur-sm hover:bg-slate-100 hover:text-slate-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:pointer-events-none sm:right-2 sm:top-2">
        <X className="h-5 w-5" aria-hidden="true" />
        <span className="sr-only">{closeLabel}</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    // 标题及右侧操作给关闭按钮留位；不改变调用方的 p-0 正文、flex 或 grid 结构。
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className, "pr-12")}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
